import React from 'react';
import { CheckCircle, FileText, Shield, Users, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const Copyright = ({ onGetStarted }) => {
  const benefits = [
    'Copyright registration with U.S. Copyright Office',
    'Work-for-hire agreement drafting',
    'Infringement analysis and enforcement',
    'DMCA takedown notice services',
    'Copyright licensing and transfers',
    'Fair use analysis and guidance',
  ];

  const process = [
    { title: 'Copyright Assessment', description: 'Review your creative work and determine optimal protection strategy.' },
    { title: 'Application Filing', description: 'Prepare and submit copyright application to U.S. Copyright Office.' },
    { title: 'Registration Processing', description: 'Monitor application status and address any office requirements.' },
    { title: 'Certificate & Protection', description: 'Receive registration certificate and implement enforcement strategy.' },
  ];

  const copyrightTypes = [
    'Literary Works',
    'Musical Compositions',
    'Software & Computer Programs',
    'Architectural Works',
    'Dramatic Works',
    'Audiovisual Content',
    'Visual Arts & Graphics',
    'Sound Recordings',
  ];

  const whyChoose = [
    { icon: Award, title: 'Registration Expertise', description: 'Extensive experience with U.S. Copyright Office procedures.' },
    { icon: Shield, title: 'Full Protection', description: 'Comprehensive copyright services from registration to enforcement.' },
    { icon: Clock, title: 'Efficient Filing', description: 'Streamlined process for faster copyright registration.' },
    { icon: TrendingUp, title: 'Strategic Counsel', description: 'Copyright strategies aligned with your creative and business goals.' },
  ];

  const faqs = [
    { question: 'What can be copyrighted?', answer: 'Copyright protects original works of authorship including literary works, music, dramatic works, artistic works, software, architectural designs, and certain databases. Ideas, facts, and titles generally cannot be copyrighted.' },
    { question: 'How long does copyright protection last?', answer: 'For works created after 1978, copyright generally lasts for the life of the author plus 70 years. For works made for hire, copyright lasts 95 years from publication or 120 years from creation, whichever is shorter.' },
    { question: 'Do I need to register my copyright?', answer: 'Copyright protection exists automatically when you create an original work. However, registration provides important legal benefits including the ability to sue for infringement and eligibility for statutory damages and attorney fees.' },
    { question: 'How much does copyright registration cost?', answer: 'U.S. Copyright Office fees range from $45 to $125 depending on the type of work and filing method. Our attorney fees vary based on the complexity and number of works being registered.' },
    { question: 'What is copyright infringement?', answer: 'Copyright infringement occurs when someone uses your copyrighted work without permission in a way that violates one of your exclusive rights (reproduction, distribution, public performance, display, or creation of derivative works).' },
  ];

  return (
    <div className="pt-20" data-testid="service-copyright">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1593444285553-28163240e3f1?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Intellectual Property Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Copyright Registration</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Secure legal protection for your creative works with professional copyright registration and enforcement services.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium" data-testid="service-cta-button">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Copyright protection is essential for creators and businesses that produce original content. Our copyright attorneys assist with registration, licensing, enforcement, and defense of your creative works.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">From literary works to software code, we protect all forms of creative expression under U.S. copyright law.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1614995008867-32eef7bff38c?w=800&q=80" alt="Creative works and copyright protection" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Types of Copyright Protection</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">We provide copyright registration services for all types of creative works.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {copyrightTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">A streamlined approach to copyright registration that ensures quality and efficiency.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1758599543132-ba9b306d715e?w=800&q=80" alt="Why choose our copyright team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Protect Your Creative Works</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for expert copyright registration and protection services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default Copyright;
